import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import PageLoader from '@/components/PageLoader';
import { routesConfig } from './RoutesConfig';
import URL from '@/pages/URL';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import HandbookAcknowledgmentHandler from '@/pages/HandbookAcknowledgmentHandler';
import EmployeeHandbook from '@/pages/EmployeeHandbook';
import AnnouncementBanner from '@/components/anncoument/AnnoucmentBanner';
import ShiftManagementApp from '@/components/attendance/ShiftEndModal';
import axios from 'axios';
import moment from 'moment';
import { Modal, Spin } from 'antd';
import { showMessage } from '@/utils/notificationHelper';
import { resetLocalStorageAfterAttendance, downloadRescueCSV } from '@/utils/attendanceUtils';
import GenerateEnergySound from '@/pages/GenerateEnergySound';
import { markOut } from '../redux/screenShorts/screenShortSaveSlice';
import { useLocalStorage } from 'react-use';
import ResetPasswordScreen from '@/pages/ResetPasswordScreen';
import { useAttendance } from '@/context/AttendanceContext';
import { filterRoutesByBackendPermissions } from '@/utils/filterRoutesByBackendPermissions';
import ProjectManagementPage from '@/pages/ProjectManagement';
import ProjectDashboardPage from '@/pages/ProjectDashboard';
const Logout = lazy(() => import(/*webpackChunkName:'LogoutPage'*/ '@/pages/Logout'));
const NotFound = lazy(() => import(/*webpackChunkName:'NotFoundPage'*/ '@/pages/NotFound'));

const SubMenuRouter = ({ subMenuRouter }) => {
  subMenuRouter.map((subMenu) => {

    return (
      <PrivateRoute
        key={subMenu.component}
        path={subMenu.path}
        exact={subMenu.exact || true}
        component={lazy(() =>
          import(/* webpackChunkName: "[request]" */ `@/pages/${subMenu.component}`)
        )}
      />
    );
  });
};

export default function AppRouter() {
  const history = useHistory();
  const location = useLocation();
  const currentAdmin = useSelector(selectCurrentAdmin);
  const [overTime, setOverTime] = useState(false);
  const [timeIn, setTimeIn] = useState(localStorage.getItem("timeIn"));
  const [timeOut, setTimeOut] = useState(localStorage.getItem("timeOut")); // ✅ now state
  const isLoggedIn = localStorage.getItem("isLoggedIn");
  const dispatch = useDispatch();
  const {
    attendance: contextAttendance,
    status: contextStatus,
    finalizeSession: contextFinalizeSession,
    stopTask,
    refreshSession
  } = useAttendance();

  const [loading, setLoading] = useState(false);
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const [lastSubmissionData, setLastSubmissionData] = useState(null);
  const [isHrNotified, setIsHrNotified] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [hasDownloadedRescue, setHasDownloadedRescue] = useState(false);




  // ✅ Memoize filteredRoutes to prevent re-calculating on every render
  const filteredRoutes = useMemo(() => {
    // If currentAdmin in Redux state isn't initialized yet after page refresh, fallback to localStorage auth
    let adminObj = currentAdmin;
    if (!adminObj?.permissions && !adminObj?.roleInfo) {
      try {
        const savedAuth = JSON.parse(localStorage.getItem('auth'));
        if (savedAuth?.result) {
          adminObj = savedAuth.result;
        }
      } catch (e) { }
    }

    return filterRoutesByBackendPermissions(
      routesConfig,
      adminObj?.permissions || [], // Safety check for logged-out users
      adminObj?.roleInfo || {}      // Safety check for logged-out users
    );
  }, [currentAdmin?.permissions, currentAdmin?.roleInfo]);


  useEffect(() => {
    const checkStatus = () => {
      const storedTimeOut = localStorage.getItem("timeOut");
      setTimeOut(storedTimeOut);
    };

    checkStatus();
    const interval = setInterval(checkStatus, 3000);
    window.addEventListener("storage", checkStatus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkStatus);
    };
  }, []);

  const handleMarkOut = async () => {
    dispatch(markOut()); // 🚀 Uploads + resets session
  };



  async function ConfirmTimeOut(overtime, overTimeRemarks) {
    console.log('[AppRouter] ConfirmTimeOut triggered. Overtime:', overtime);

    // 1. Stop any active task via context if it exists
    let currentAttendance = contextAttendance;
    console.log('[AppRouter] Initial contextAttendance dailyTask count:', contextAttendance?.dailyTask?.length || 0);

    if (contextAttendance?.activeTaskId) {
      console.log('[AppRouter] Active task detected (ID:', contextAttendance.activeTaskId, '). Stopping before finalization...');
      try {
        const stopResult = await stopTask(); // useAttendance hook export
        console.log('[AppRouter] stopTask result success:', stopResult?.success, 'New dailyTask count:', stopResult?.attendance?.dailyTask?.length || 0);
        if (stopResult?.success && stopResult.attendance) {
          currentAttendance = stopResult.attendance;
        }
      } catch (err) {
        console.error('[AppRouter] Failed to stop active task during timeout:', err);
      }
    }

    // 2. Refresh session to get the absolute latest task data from DB
    try {
      console.log('[AppRouter] Refreshing session to sync with DB source of truth...');
      const refreshedSession = await refreshSession();
      console.log('[AppRouter] refreshSession result new dailyTask count:', refreshedSession?.dailyTask?.length || 0);
      if (refreshedSession) {
        currentAttendance = refreshedSession;
      }
    } catch (err) {
      console.warn('[AppRouter] Session refresh failed, falling back to existing context state.', err);
    }

    // 3. Prepare payload using Context/Backend data (Source of Truth)
    const dailyTask = currentAttendance?.dailyTask || [];
    console.log('[AppRouter] Final dailyTask count for payload:', dailyTask.length);
    const tomorrowTask = JSON.parse(localStorage.getItem('tomorrowTask') || '[]') || [];
    const notesList = JSON.parse(localStorage.getItem('notesList') || '[]') || [];
    const notes = JSON.parse(localStorage.getItem('notes') || '[]') || [];
    const attendanceTimeInValue = currentAttendance?.timeIn || localStorage.getItem('timeIn');

    // Break time calculation (Keeping legacy calc for now if context doesn't have it, but prioritizing context)
    const breakStartTime = localStorage.getItem("breakStartTime");
    const breakEndTime = localStorage.getItem("breakEndTime");
    const breakTimeValue = (breakEndTime && breakStartTime)
      ? ((new Date(breakEndTime) - new Date(breakStartTime)) / 1000).toFixed(3)
      : 0;

    const newTimeOut = moment();

    // Calculate elapsed time for UI display
    if (attendanceTimeInValue) {
      const duration = moment.duration(newTimeOut.diff(moment(attendanceTimeInValue)));
      localStorage.setItem('elapsedTime', `${Math.floor(duration.asHours())}h ${Math.floor(duration.asMinutes()) % 60}m`);
    }

    // Play feedback audio
    new Audio('/livechat-129007.mp3').play().catch(() => { });

    const payload = {
      timeIn: attendanceTimeInValue,
      timeOut: newTimeOut.toISOString(),
      breakTime: breakTimeValue,
      dailyTask: dailyTask, // NOW COMES FROM CONTEXT/DB
      tomorrowTask,
      notes: notesList.length ? notes : [],
      overtTime: overtime === '00:00:00' ? null : overtime,
      overTimeRemarks: overTimeRemarks
    };

    console.log('[AppRouter] Submitting Shift End Payload:', payload);

    setLastSubmissionData(payload);
    setIsSubmissionError(false);
    setIsGlobalLoading(true);

    try {
      const response = await contextFinalizeSession(payload);

      if (response && response.success) {
        dispatch(markOut());

        setTimeout(() => {
          resetLocalStorageAfterAttendance();
          localStorage.setItem('attendanceStatus', 'out');
          localStorage.setItem('timeOut', newTimeOut.toISOString());
          setTimeOut(newTimeOut.toISOString());
          setIsGlobalLoading(false);
          setRetryCount(0);
          history.push('/');
        }, 1500);
      } else {
        throw new Error(response?.error?.response?.data?.message || 'Attendance auto-submission failed.');
      }
    } catch (err) {
      setIsSubmissionError(true);
      setRetryCount(prev => prev + 1);
      dispatch(markOut());

      const errorMsg = err.message || 'Attendance auto-submission failed.';
      showMessage.error(errorMsg);
      console.error('[AppRouter] Finalize failed:', err);
    }
  }


  const notifyHr = async () => {
    try {
      await axios.post('/attendance/notify-hr-failure', {
        userName: currentAdmin.name,
        userEmail: currentAdmin.email,
        error: 'Network/Server Error during automated mark-out'
      });
      setIsHrNotified(true);
      showMessage.success('HR has been notified of the failure.');
    } catch (error) {
      console.error('Failed to notify HR:', error);
      showMessage.error('Failed to send notification to HR.');
    }
  };


  const fetchPermissions = async () => {
    try {
      setLoading(true);

      // Make API call to get current permissions and role info
      const response = await axios.get('/getCurrentPermissionsWithRoleInfo');
      if (response.data.success) {
        // Get current user data from localStorage
        const currentUserData = JSON.parse(localStorage.getItem('auth')) || {};

        // Update with new roleInfo, permissions, and workDetails
        const updatedUserData = {
          ...currentUserData,
          roleInfo: response.data.result.roleInfo,
          permissions: response.data.result.permissions,
          photo: response.data?.result?.photo,
          profileStatus: response.data?.result?.profileStatus,
          profileType: response.data?.result?.profileType,
          workLocation: response.data?.result?.workLocation,
        };

        // Save updated data back to localStorage
        localStorage.setItem('auth', JSON.stringify(updatedUserData));

        // Update Redux store with new data including workDetails
        const authData = JSON.parse(localStorage.getItem('auth'));
        if (authData?.result) {
          // Import and use the actual Redux store
          try {
            const store = require('@/redux/store').default;
            store.dispatch({
              type: 'SET_CURRENT_ADMIN',
              payload: authData.result
            });
          } catch (e) {
            console.log('Redux store not accessible, localStorage updated only');
          }
        }

        console.log('Permissions updated successfully');
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Handle error appropriately
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);


  // ✅ Memoize component routes to prevent chunk load issues during dev server updates
  const memoizedRoutes = useMemo(() => {
    return filteredRoutes.map((routeItem) => {
      let Component;
      if (routeItem.component === 'ProjectDashboard') {
        Component = ProjectDashboardPage;
      } else if (routeItem.component === 'ProjectManagement') {
        Component = ProjectManagementPage;
      } else {
        Component = lazy(() => import(`@/pages/${routeItem.component}`));
      }
      return {
        ...routeItem,
        Component
      };
    });
  }, [filteredRoutes]);

  return (
    <Suspense fallback={<PageLoader />}>
      {isGlobalLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          {isSubmissionError ? (
            <div style={{
              maxWidth: 450,
              padding: 32,
              background: '#fff',
              borderRadius: 12,
              textAlign: 'center',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }}>⚠️</div>
              <h2 style={{ marginBottom: 12 }}>Auto-Submission Failed</h2>
              <p style={{ color: '#666', marginBottom: 24 }}>
                Your shift ended and we tried to save your attendance, but the server is unreachable.
                Your work data is <strong>safely stored</strong> on this computer.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button
                  onClick={() => ConfirmTimeOut(lastSubmissionData.overtTime)}
                  disabled={!navigator.onLine}
                  style={{
                    padding: '12px',
                    background: '#1890ff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: !navigator.onLine ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    opacity: !navigator.onLine ? 0.6 : 1
                  }}
                >
                  Retry Submission
                </button>
                <button
                  onClick={() => {
                    downloadRescueCSV(lastSubmissionData, currentAdmin.name, currentAdmin);
                    setHasDownloadedRescue(true);
                  }}
                  style={{
                    padding: '12px',
                    background: '#f5f5f5',
                    color: '#333',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    cursor: 'pointer'
                  }}
                >
                  Download Rescue CSV (Local Recovery) {hasDownloadedRescue && '✅'}
                </button>
                <div style={{ fontSize: '11px', color: '#888', margin: '4px 0' }}>
                  {retryCount < 3
                    ? `You can discard data after ${3 - retryCount} more retry attempts.`
                    : !hasDownloadedRescue
                      ? "Download the CSV to enable the Discard option."
                      : "Ready to Reset?"}
                </div>
                <button
                  onClick={() => {
                    Modal.confirm({
                      title: "Discard unsaved session data?",
                      content: "This will clear your current local logs. Ensure you have downloaded your Rescue CSV first!",
                      okText: "Yes, Discard",
                      cancelText: "No, Keep Data",
                      okType: 'danger',
                      centered: true,
                      zIndex: 10001,
                      onOk: () => {
                        resetLocalStorageAfterAttendance();
                        localStorage.setItem('attendanceStatus', 'out');
                        setIsGlobalLoading(false);
                        setIsSubmissionError(false);
                        setRetryCount(0);
                        setHasDownloadedRescue(false);
                        history.push('/');
                      }
                    });
                  }}
                  disabled={retryCount < 3 || !hasDownloadedRescue}
                  style={{
                    padding: '8px',
                    background: 'transparent',
                    color: (retryCount < 3 || !hasDownloadedRescue) ? '#ccc' : '#ff4d4f',
                    border: 'none',
                    cursor: (retryCount < 3 || !hasDownloadedRescue) ? 'not-allowed' : 'pointer',
                    fontWeight: (retryCount < 3 || !hasDownloadedRescue) ? 'normal' : 'bold'
                  }}
                >
                  Discard & Start Fresh
                </button>
                <button
                  onClick={() => {
                    setIsGlobalLoading(false);
                    setIsSubmissionError(false);
                  }}
                  style={{
                    padding: '8px',
                    background: 'transparent',
                    color: '#1890ff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Return to Dashboard
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <Spin size="large" tip="Cleaning up and saving your session..." />
            </div>
          )}
        </div>
      )}
      <AnimatePresence exitBeforeEnter initial={false}>
        {(() => {
          if (contextStatus === 'in') {
            const attId = contextAttendance?._id || 'NO_ID';
            if (window._lastStatusLog !== `${contextStatus}-${attId}`) {
              window._lastStatusLog = `${contextStatus}-${attId}`;
              console.log(`[AppRouter] Rendering ShiftManagementApp. Status: ${contextStatus}, AttID: ${attId}`);
            }
          }
          return contextStatus === 'in' ? (
            <ShiftManagementApp
              setOverTime={setOverTime}
              markTimeOut={ConfirmTimeOut}
              timeIn={contextAttendance?.timeIn || timeIn}
            />
          ) : null;
        })()}
        {
          currentAdmin?.trackingType == 'hourly' && contextStatus === 'in' ?
            <GenerateEnergySound /> : null
        }
        <Switch location={location} key="app-shell">

          {/* ── Public routes — MUST come before PrivateRoute map ────────── */}
          {/* These are checked first so they never hit the auth guard        */}
          <Route path="/reset-password/:token" component={ResetPasswordScreen} exact />
          <Route path="/handbook/acknowledge/:token" component={HandbookAcknowledgmentHandler} exact />
          <Route path="/employee-handbook" component={EmployeeHandbook} exact />
          <Route path="/shared/task/:token" component={lazy(() => import('@/pages/SharedTaskView'))} exact />

          {/* Marketing Tools — public, no login required */}
          <Route
            path="/seo-snippet"
            component={lazy(() => import('@/pages/SeoSnippetEmbed'))}
            exact
          />
          <Route
            path="/google-ads"
            component={lazy(() => import('@/pages/GoogleAdsEmbed'))}
            exact
          />

          {/* ── Project routes — single component instance for both paths ── */}
          {/* Using an array of paths prevents React Router from remounting   */}
          {/* the component when switching between /project-management and    */}
          {/* /project-dashboard via the sidebar.                             */}
          <PrivateRoute
            key="project-shell"
            path={['/project-management', '/project-dashboard']}
            component={ProjectManagementPage}
          />

          {memoizedRoutes
            .filter(r => r.component !== 'ProjectManagement' && r.component !== 'ProjectDashboard')
            .map((routeItem) => (
            <PrivateRoute
              key={routeItem.component}
              path={routeItem.path}
              exact={routeItem.exact || true}
              component={routeItem.Component}
            />
          ))}

          <PublicRoute path="/login" render={() => <Redirect to="/" />} exact />
          <Route component={Logout} path="/logout" exact />
          <Route path="/url/:id" component={URL} />
          <Route path="*" component={NotFound} render={() => <Redirect to="/notfound" />} />
        </Switch>
      </AnimatePresence>
    </Suspense>
  );
}
